import { summarizeErrors } from './member-form-errors';

export default (config = {}) => ({
  submitting: false,
  successMessage: '',
  country: config.country || 'United States',
  state: config.state || '',
  usStates: Array.isArray(config.usStates) ? config.usStates : [],
  canadianProvinces: Array.isArray(config.canadianProvinces) ? config.canadianProvinces : [],
  headline: config.headline || '',
  selectedSkills: Array.isArray(config.selectedSkills) ? config.selectedSkills : [],
  spokenLanguages: Array.isArray(config.spokenLanguages) ? config.spokenLanguages : [],
  additionalLanguageInfo: config.additionalLanguageInfo || '',
  displayProfileInformationPublicly: Boolean(config.displayProfileInformationPublicly),
  displayEmailAddress: Boolean(config.displayEmailAddress),
  newsroom: Boolean(config.newsroom),
  newsroomTellUsMore: config.newsroomTellUsMore || '',
  awardsCertificates: [],
  featuredWork: [],
  rowKey: 0,

  init() {
    this.awardsCertificates = this.normalizeRows(config.awardsCertificates, [
      'awardCertificateName',
    ]);
    this.featuredWork = this.normalizeRows(config.featuredWork, [
      'videoYoutubeUrl',
      'videoTitle',
      'videoDescription',
    ]);
  },

  get subdivisionOptions() {
    if (this.country === 'United States') {
      return this.usStates;
    }
    if (this.country === 'Canada') {
      return this.canadianProvinces;
    }
    return [];
  },

  get hasSubdivisionOptions() {
    return this.subdivisionOptions.length > 0;
  },

  get subdivisionLabel() {
    return this.country === 'Canada' ? 'Province / Territory' : 'State';
  },

  get subdivisionPlaceholder() {
    return this.country === 'Canada' ? 'Select a province or territory' : 'Select a state';
  },

  get hasAdditionalLanguages() {
    return this.spokenLanguages.includes('other');
  },

  // The shared option-field macro delegates its native selection limits to the owning Alpine component.
  validateCheckboxGroup(group) {
    if (!group) {
      return true;
    }

    const checkboxes = Array.from(group.querySelectorAll('input[type="checkbox"]'));
    const firstCheckbox = checkboxes[0];
    if (!firstCheckbox) {
      return true;
    }

    const checkedCount = checkboxes.filter((checkbox) => checkbox.checked).length;
    const minimum = Number(group.dataset.minimum || 0);
    const maximum = Number(group.dataset.maximum || 0);
    let message = '';

    if (minimum && checkedCount < minimum) {
      message = `Select at least ${minimum} option${minimum === 1 ? '' : 's'}.`;
    } else if (maximum && checkedCount > maximum) {
      message = `Select no more than ${maximum} options.`;
    }

    firstCheckbox.setCustomValidity(message);
    return message === '';
  },

  handleSpokenLanguagesChange() {
    if (!this.hasAdditionalLanguages) {
      // Do not submit a hidden value after the controlling Other option is removed.
      this.additionalLanguageInfo = '';
    }
  },

  handleProfileVisibilityChange() {
    if (!this.displayProfileInformationPublicly) {
      // A hidden profile cannot expose its email independently.
      this.displayEmailAddress = false;
    }
  },

  handleNewsroomChange() {
    if (!this.newsroom) {
      // Newsroom details should not survive after the controlling membership flag is removed.
      this.newsroomTellUsMore = '';
    }
  },

  handleCountryChange() {
    // A subdivision code only has meaning within its selected country.
    this.state = '';
  },

  addAward() {
    this.awardsCertificates.push(this.createRow(['awardCertificateName']));
  },

  removeAward(index) {
    this.awardsCertificates.splice(index, 1);
  },

  addFeaturedWork() {
    if (this.featuredWork.length >= 4) {
      return;
    }

    this.featuredWork.push(this.createRow([
      'videoYoutubeUrl',
      'videoTitle',
      'videoDescription',
    ]));
  },

  removeFeaturedWork(index) {
    this.featuredWork.splice(index, 1);
  },

  normalizeRows(rows, fields) {
    if (!Array.isArray(rows)) {
      return [];
    }

    return rows.map((row) => {
      const normalized = this.createRow(fields);
      fields.forEach((field) => {
        normalized[field] = typeof row?.[field] === 'string' ? row[field] : '';
      });
      return normalized;
    });
  },

  createRow(fields) {
    // Stable keys prevent Alpine from reusing a later row's inputs when an earlier row is removed.
    const row = {key: `member-profile-row-${this.rowKey++}`};
    fields.forEach((field) => {
      row[field] = '';
    });
    return row;
  },

  async submitProfile(event) {
    event.preventDefault();

    if (this.submitting) {
      return;
    }

    this.submitting = true;
    this.successMessage = '';
    this.clearErrors();

    try {
      const formData = new FormData(this.$root);
      // The fetch URL owns AJAX routing; keep Craft's native fallback controls out of its payload.
      formData.delete('action');
      formData.delete('redirect');

      // An empty repeater has no rendered controls, so explicitly submit it to clear any
      // previously saved Craft Table rows instead of treating that attribute as omitted.
      if (this.awardsCertificates.length === 0) {
        formData.set('memberProfile[awardsCertificates]', '');
      }
      if (this.featuredWork.length === 0) {
        formData.set('memberProfile[featuredWork]', '');
      }

      const response = await fetch(config.endpoint, {
        method: 'POST',
        body: formData,
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
      });
      const result = await response.json();

      if (!response.ok || result?.valid !== true || result?.saved !== true) {
        this.renderErrors(Object.keys(result?.errors || {}).length
          ? result.errors
          : {form: [result?.message || 'We couldn’t save your profile. Please try again.']});
        return;
      }

      this.successMessage = result.message || 'Edits saved.';
      this.$nextTick(() => this.$refs.profileSuccess?.focus({preventScroll: true}));
    } catch (error) {
      this.renderErrors({
        form: ['We couldn’t save your profile. Please refresh the page and try again.'],
      });
    } finally {
      this.submitting = false;
    }
  },

  clearErrors() {
    this.$root.querySelector('[data-member-error-summary]')?.remove();
    // Detach only client-rendered errors, preserving IDs for server-rendered instructions.
    this.$root.querySelectorAll('.fui-error-messages').forEach((errorContainer) => {
      const errorId = errorContainer.id;
      if (errorId) {
        this.$root.querySelectorAll('[aria-describedby]').forEach((control) => {
          const describedBy = (control.getAttribute('aria-describedby') || '')
            .split(/\s+/)
            .filter((id) => id && id !== errorId);
          if (describedBy.length) {
            control.setAttribute('aria-describedby', describedBy.join(' '));
          } else {
            control.removeAttribute('aria-describedby');
          }
        });
      }
      errorContainer.remove();
    });
    this.$root.querySelectorAll('[aria-invalid="true"]')
      .forEach((control) => control.removeAttribute('aria-invalid'));
  },

  renderErrors(errors) {
    Object.entries(errors).forEach(([attribute, messages]) => {
      if (!Array.isArray(messages) || messages.length === 0) {
        return;
      }

      const attributePath = attribute.split('.');
      const rootAttribute = attributePath.shift();
      const rootField = Array.from(this.$root.querySelectorAll('[data-field-handle]'))
        .find((candidate) => candidate.dataset.fieldHandle === rootAttribute);
      // Dotted backend paths identify the exact indexed input inside an Alpine repeater.
      const expectedName = `memberProfile[${rootAttribute}]${attributePath.map((part) => `[${part}]`).join('')}`;
      const control = Array.from(this.$root.querySelectorAll('input:not([type="hidden"]), select, textarea'))
        .find((candidate) => attributePath.length
          ? candidate.name === expectedName
          : candidate.name === expectedName || candidate.name.startsWith(`${expectedName}[`));
      const field = attributePath.length ? control?.closest('.fui-field') : rootField;
      // Unexpected Craft User errors remain visible in the form summary instead of being lost.
      const target = field || this.$root.querySelector('[data-profile-form-errors]');
      if (!target) {
        return;
      }

      const errorId = `member-profile-${attribute.replace(/[^a-zA-Z0-9_-]+/g, '-')}-errors`;
      const errorContainer = document.createElement('div');
      errorContainer.id = errorId;
      errorContainer.className = 'fui-error-messages';
      errorContainer.dataset.profileError = '';
      errorContainer.setAttribute('role', 'alert');
      errorContainer.setAttribute('tabindex', '-1');

      messages.forEach((message) => {
        const paragraph = document.createElement('p');
        paragraph.className = 'fui-error-message';
        paragraph.textContent = message;
        errorContainer.append(paragraph);
      });
      target.append(errorContainer);

      if (control) {
        control.setAttribute('aria-invalid', 'true');
        const describedBy = new Set(
          (control.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean),
        );
        describedBy.add(errorId);
        control.setAttribute('aria-describedby', Array.from(describedBy).join(' '));
      }
    });

    summarizeErrors(this.$root);

    const focusTarget = this.$root.querySelector('[aria-invalid="true"], [data-profile-error]');
    if (focusTarget) {
      focusTarget.focus({preventScroll: true});
      focusTarget.scrollIntoView({behavior: 'smooth', block: 'center'});
    }
  },
});
